import { Injectable } from "@angular/core";
import { Observable, of, ReplaySubject } from "rxjs";
import {
    AccountConfigurationAuth0Minimal,
    AccountService,
    AdminService,
    LogsService,
    Report,
    ReportsService,
    UsersService,
    AccountDemoType,
} from "@hydrantid/acm-client";
import { map, tap } from "rxjs/operators";
import { AzureEndpoint } from "../../modules/accounts/components/models/azure-endpoint";
import { environment } from "../../../configuration";

@Injectable({
    providedIn: "root",
})
export class CachedDataService {
    private subscriptionTypes: Observable<string[]> | undefined;
    private connectionInfo: Record<string, AccountConfigurationAuth0Minimal> = {};
    private reportsByAccountID: Record<string, ReplaySubject<Report[]>> = {};
    private accountDemoTypesAndTemplates: ReplaySubject<AccountDemoType[]> | undefined;
    private auth0SigningCert: ReplaySubject<string> | undefined;

    constructor(
        private logsService: LogsService,
        private usersService: UsersService,
        private reportsService: ReportsService,
        private accountService: AccountService,
        private adminService: AdminService,
    ) {}

    get subscriptionTypes$(): Observable<string[]> {
        if (!this.subscriptionTypes) {
            const subscriptionTypes: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
            this.logsService.logsSubscriptionTypesGet().subscribe((results) => subscriptionTypes.next(results));
            this.subscriptionTypes = subscriptionTypes.asObservable();
        }
        return this.subscriptionTypes;
    }

    getConnectionInfoForSlug(slug: string): Observable<AccountConfigurationAuth0Minimal> {
        if (slug in this.connectionInfo) {
            return of(this.connectionInfo[slug]);
        }
        return this.usersService.usersConnectionSlugGet(slug).pipe(
            tap((connectionInfo) => {
                this.connectionInfo[slug!] = connectionInfo;
            }),
        );
    }

    getReportsForAccount(accountID: string): Observable<Report[]> {
        const cachedReports = this.reportsByAccountID[accountID];
        if (cachedReports) {
            return cachedReports.asObservable();
        }

        const reports = new ReplaySubject<Report[]>(1);
        this.reportsByAccountID[accountID] = reports;
        this.reportsService.listReports(accountID).subscribe({
            next: (result) => reports.next(result),
            error: (error) => {
                // Evict before notifying callers so another request can retry immediately.
                delete this.reportsByAccountID[accountID];
                reports.error(error);
            },
        });
        return reports.asObservable();
    }

    get azureEndpoints$(): Observable<AzureEndpoint[]> {
        return of(environment.azureEndpoints);
    }

    get auth0SigningCert$(): Observable<string> {
        if (!this.auth0SigningCert) {
            this.auth0SigningCert = new ReplaySubject<string>(1);
            this.adminService.adminAccountConfigurationAuth0SigningCertGet().subscribe((cert) => {
                this.auth0SigningCert!.next(cert);
            });
        }
        return this.auth0SigningCert.asObservable();
    }

    get accountDemoTypesAndTemplates$(): Observable<AccountDemoType[]> {
        if (!this.accountDemoTypesAndTemplates) {
            this.accountDemoTypesAndTemplates = new ReplaySubject<AccountDemoType[]>(1);
            this.accountService.accountsDemoTypesGet().subscribe({
                next: (result) => {
                    this.accountDemoTypesAndTemplates!.next(result);
                },
            });
        }
        return this.accountDemoTypesAndTemplates.asObservable();
    }
    get accountTemplates$(): Observable<AccountDemoType[]> {
        return this.accountDemoTypesAndTemplates$.pipe(
            map((types) => types.filter((t) => !t.demoType.startsWith("demo-"))),
        );
    }

    get accountDemoTypes$(): Observable<AccountDemoType[]> {
        return this.accountDemoTypesAndTemplates$.pipe(
            map((types) => types.filter((t) => t.demoType.startsWith("demo-"))),
        );
    }
}
